import React, { useEffect, useMemo, useState } from "react";
import { AdminPanelSettings, Groups, LockPerson, ManageAccounts } from "@mui/icons-material";
import { permissionGroups, roleTemplates, settingsGroups } from "../../../../data/workspaceData";
import { navItems } from "../../../../data/workspaceConfig";
import { SectionHeader } from "../../common/WorkspaceChrome";
import { getWorkspaceOwnershipCandidates, transferWorkspaceOwnership } from "../../../../services/workspaceApi";

const DEFAULT_WORKSPACE_ICON = "/icons/lyriclious-icon.png";
const getDisplayedWorkspacePlan = (plan) => plan || "Free";
const developerLockedModuleKeys = new Set(navItems.filter((item) => item.comingSoon === true).map((item) => item.label));
const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(new Error("Could not read file"));
    reader.readAsDataURL(file);
  });

function ModuleArrangementPanel({ modules, onSave }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const configured = new Map((modules || []).map((module) => [module.key, module]));
    const fallback = navItems.filter((item) => item.sidebarHidden !== true).map((item, index) => ({ key: item.label, label: item.label, order: index + 1, enabled: true, premium: false, tabs: [], required: ["Home", "Administration"].includes(item.label) }));
    const configuredOnly = (modules || [])
      .filter((module) => module?.key && !fallback.some((fallbackModule) => fallbackModule.key === module.key))
      .map((module, index) => ({ ...module, order: module.order || fallback.length + index + 1, developerLocked: false, required: false }));
    const configuredFallbackRows = fallback
      .map((module) => ({ ...module, ...configured.get(module.key), developerLocked: developerLockedModuleKeys.has(module.key), required: ["Home", "Administration"].includes(module.key) }));
    setRows([...configuredFallbackRows, ...configuredOnly]
      .sort((a, b) => (a.developerLocked === b.developerLocked ? (a.order || 0) - (b.order || 0) : a.developerLocked ? 1 : -1)));
  }, [modules]);

  const move = (key, direction) => setRows((current) => {
    const index = current.findIndex((module) => module.key === key);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= current.length || current[index].developerLocked || current[target].developerLocked || current[index].required || current[target].required) return current;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });

  const toggleEnabled = (key) => setRows((current) => current.map((module) => module.key === key && !module.developerLocked && !module.required ? { ...module, enabled: module.enabled === false } : module));

  const save = async () => {
    setSaving(true); setMessage("");
    try {
      const hiddenModules = (modules || []).filter((module) => module.key === "Projects");
      await onSave?.({ modules: [...rows.map((module, index) => ({ ...module, order: index + 1 })), ...hiddenModules.map((module, index) => ({ ...module, order: rows.length + index + 1 }))] });
      setMessage("Navigation order saved.");
    } catch (error) { setMessage(error?.response?.data?.message || "Could not save navigation order."); }
    finally { setSaving(false); }
  };

  return <section className="panel module-arrangement-panel"><SectionHeader eyebrow="Navigation" title="Sections and order" text="Show released sections your team needs and arrange their sidebar order. Home and Administration stay available; Lyriclious-locked sections cannot be changed." /><div className="module-arrangement-list">{rows.map((module, index) => <article key={module.key} className={module.developerLocked ? "locked" : ""}><div><strong>{module.label || module.key}</strong><small>{module.developerLocked ? "Locked by Lyriclious development" : module.required ? "Required workspace section" : module.enabled === false ? "Hidden from workspace navigation" : "Visible in workspace navigation"}</small></div><div><button aria-label={`${module.enabled === false ? "Show" : "Hide"} ${module.label}`} aria-pressed={module.enabled !== false} className="module-visibility-toggle" disabled={saving || module.developerLocked || module.required} onClick={() => toggleEnabled(module.key)} type="button">{module.enabled === false ? "Show" : "Visible"}</button><button aria-label={`Move ${module.label} up`} disabled={saving || module.developerLocked || module.required || index === 0 || rows[index - 1]?.developerLocked || rows[index - 1]?.required} onClick={() => move(module.key, -1)} type="button">↑</button><button aria-label={`Move ${module.label} down`} disabled={saving || module.developerLocked || module.required || index === rows.length - 1 || rows[index + 1]?.developerLocked || rows[index + 1]?.required} onClick={() => move(module.key, 1)} type="button">↓</button></div></article>)}</div><div className="workspace-dialog-actions"><button disabled={saving} onClick={save} type="button">{saving ? "Saving…" : "Save workspace navigation"}</button></div>{message ? <p className="project-settings-status" role="status">{message}</p> : null}</section>;
}

function WorkspaceIdentityPanel({ currentUser, onSave, workspaceInfo }) {
  const [name, setName] = useState(workspaceInfo?.name || "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => setName(workspaceInfo?.name || ""), [workspaceInfo?.name]);
  const save = async (event) => {
    event.preventDefault();
    const nextName = name.trim();
    if (!nextName) { setMessage("Workspace name is required."); return; }
    setSaving(true); setMessage("");
    try { await onSave?.({ workspaceName: nextName }); setMessage("Workspace identity saved."); }
    catch (error) { setMessage(error?.response?.data?.message || "Could not save workspace identity."); }
    finally { setSaving(false); }
  };
  const workspaceUrl = typeof window === "undefined" ? `/${workspaceInfo?.slug || ""}` : `${window.location.origin}/${workspaceInfo?.slug || ""}`;
  return <section className="panel workspace-identity-panel"><SectionHeader eyebrow="Workspace identity" title="Name and workspace address" text="The workspace name appears across collaboration surfaces. Its address remains stable so existing links and invitations continue to work." /><form onSubmit={save}><label>Workspace name<input disabled={saving} maxLength="120" onChange={(event) => setName(event.target.value)} required value={name} /></label><label>Workspace address<input aria-label="Workspace address" disabled value={workspaceUrl} /></label><div className="workspace-identity-meta"><span>Plan <strong>{getDisplayedWorkspacePlan(workspaceInfo?.plan)}</strong></span><span>Owner <strong>{currentUser?.accessLevel === "Owner" ? "You" : "Workspace owner"}</strong></span></div><div className="workspace-dialog-actions"><button disabled={saving || !onSave || name.trim() === (workspaceInfo?.name || "").trim()} type="submit">{saving ? "Saving…" : "Save identity"}</button></div>{message ? <p className="project-settings-status" role="status">{message}</p> : null}</form></section>;
}

function WorkspaceStructurePanel({ administration, onNavigateModule }) {
  const departments = administration?.departments || [];
  const teams = administration?.teams || [];
  return <section className="panel workspace-structure-panel"><SectionHeader eyebrow="Operating structure" title="Departments and teams" text="Structure is managed in People so assignments, access, and reporting all use the same source of truth." /><div className="workspace-structure-metrics"><article><strong>{departments.filter((department) => department.status !== "Inactive").length}</strong><span>active departments</span><button onClick={() => onNavigateModule("People", "Departments")} type="button">Manage departments</button></article><article><strong>{teams.filter((team) => team.status !== "Inactive").length}</strong><span>active teams</span><button onClick={() => onNavigateModule("People", "Teams")} type="button">Manage teams</button></article></div></section>;
}

function WorkspaceOwnershipPanel({ currentUser, onTransferred }) {
  const isOwner = currentUser?.accessLevel === "Owner";
  const [candidates, setCandidates] = useState([]);
  const [targetUserId, setTargetUserId] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!isOwner) return;
    getWorkspaceOwnershipCandidates().then((data) => setCandidates(data.candidates || [])).catch(() => setMessage("Could not load eligible members."));
  }, [isOwner]);
  if (!isOwner) return null;
  const transfer = async (event) => {
    event.preventDefault(); setLoading(true); setMessage("");
    try {
      const data = await transferWorkspaceOwnership({ targetUserId, confirmation });
      setMessage(data.message); onTransferred?.();
    } catch (error) { setMessage(error?.response?.data?.message || "Ownership could not be transferred."); }
    finally { setLoading(false); }
  };
  return <section className="panel workspace-ownership-panel"><SectionHeader eyebrow="Sensitive action" title="Transfer workspace ownership" text="The new owner receives full control. You remain an admin, but lose ownership, billing, and recovery authority." /><form onSubmit={transfer}><label>New owner<select disabled={loading || !candidates.length} onChange={(event) => setTargetUserId(event.target.value)} required value={targetUserId}><option value="">Select an active member</option>{candidates.map((person) => <option key={person.id} value={person.id}>{person.name} · {person.email}</option>)}</select></label><label>Type <strong>TRANSFER OWNERSHIP</strong> to confirm<input disabled={loading} onChange={(event) => setConfirmation(event.target.value)} required value={confirmation} /></label><div className="workspace-dialog-actions"><button disabled={loading || !targetUserId || confirmation !== "TRANSFER OWNERSHIP"} type="submit">{loading ? "Transferring…" : "Transfer ownership"}</button></div>{message ? <p className="project-settings-status" role="status">{message}</p> : null}</form></section>;
}

function WorkspaceAdministrationPanel({ administration, currentUser, onNavigateModule, onRefresh, onSave, workspaceInfo }) {
  const isOwner = currentUser?.accessLevel === "Owner";
  return <div className="workspace-administration-panel">{isOwner ? <><WorkspaceIdentityPanel currentUser={currentUser} onSave={onSave} workspaceInfo={workspaceInfo} /><ModuleArrangementPanel modules={administration?.modules || []} onSave={onSave} /><WorkspaceOwnershipPanel currentUser={currentUser} onTransferred={onRefresh} /></> : <section className="panel"><p className="empty-state">Workspace configuration is visible here, but only the workspace owner can update it.</p></section>}<WorkspaceStructurePanel administration={administration} onNavigateModule={onNavigateModule} /></div>;
}

function WorkspaceBillingPanel({ administration, currentUser, onSave, workspaceInfo }) {
  const rawPlan = workspaceInfo?.plan || "Free";
  const plan = rawPlan === "Free" ? "Free" : "Workspace Pro";
  const byokEnabled = workspaceInfo?.aiByokEnabled === true;
  const billing = workspaceInfo?.billing || {};
  const perEmployeeMonthlyAmount = Number(billing.perEmployeeMonthlyAmount || 49);
  const byokMonthlyAmount = Number(billing.byokMonthlyAmount || 499);
  const usage = administration?.overview?.usage || {};
  const seats = usage.seats || { used: 0, limit: workspaceInfo?.limits?.freeEmployeeLimit || 0 };
  const projects = usage.projects || { used: 0, limit: workspaceInfo?.limits?.freeProjectLimit || 0 };
  const isOwner = currentUser?.accessLevel === "Owner";
  const canManageBilling = currentUser?.canManageWorkspace === true || isOwner || /\b(admin|owner)\b/i.test(`${currentUser?.accessLevel || ""} ${currentUser?.workspaceRole || ""}`);
  const [pendingBillingChange, setPendingBillingChange] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const usageLabel = (metric) => metric.limit > 0 ? `${metric.used || 0} of ${metric.limit}` : `${metric.used || 0} in use`;
  const overFreeSeats = Number(seats.limit || 0) > 0 && Number(seats.used || 0) > Number(seats.limit || 0);
  const overFreeProjects = Number(projects.limit || 0) > 0 && Number(projects.used || 0) > Number(projects.limit || 0);
  const freeDowngradeBlocked = plan === "Workspace Pro" && (overFreeSeats || overFreeProjects);

  useEffect(() => {
    setPendingBillingChange(null);
  }, [plan, byokEnabled]);

  const applyBillingChange = async () => {
    if (!onSave || !pendingBillingChange) return;
    setSaving(true); setMessage("");
    try {
      await onSave({ plan: pendingBillingChange.nextPlan, aiByokEnabled: pendingBillingChange.nextByokEnabled });
      setMessage("Billing preferences updated.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not update billing preferences.");
    } finally {
      setSaving(false);
      setPendingBillingChange(null);
    }
  };

  const openPlanConfirmation = (enabled) => {
    if (enabled && plan === "Workspace Pro") return;
    if (!enabled && plan === "Free") return;
    if (!enabled && freeDowngradeBlocked) {
      setMessage(`Free is unavailable while usage is above the free quota: ${usageLabel(seats)} people and ${usageLabel(projects)} projects.`);
      return;
    }
    setPendingBillingChange({
      title: enabled ? "Upgrade to Workspace Pro?" : "Move workspace back to Free?",
      nextPlan: enabled ? "Workspace Pro" : "Free",
      nextByokEnabled: byokEnabled,
      actionLabel: enabled ? "Upgrade to Pro" : "Move to Free",
      summary: enabled
        ? `Workspace Pro keeps your current workspace active and bills ₹${perEmployeeMonthlyAmount.toLocaleString("en-IN")} per additional active person above the included free quota.`
        : `Free will enforce the included limits: ${seats.limit || 0} people and ${projects.limit || 0} project${Number(projects.limit || 0) === 1 ? "" : "s"}.`,
      impact: enabled
        ? "Your team can continue adding people and projects beyond the free allowance."
        : "Any future people or project creation above the free allowance will be blocked.",
    });
  };

  const openByokConfirmation = (enabled) => {
    if (enabled === byokEnabled) return;
    setPendingBillingChange({
      title: enabled ? "Enable AI tools add-on?" : "Disable AI tools add-on?",
      nextPlan: plan,
      nextByokEnabled: enabled,
      actionLabel: enabled ? "Enable AI tools" : "Disable AI tools",
      summary: enabled
        ? `This adds BYOK AI tooling at ₹${byokMonthlyAmount.toLocaleString("en-IN")}/month. The workspace still needs an API key connected by an admin.`
        : "AI tools using the workspace BYOK connection will be turned off for this workspace.",
      impact: enabled ? "AI suggestions and assistant surfaces can use the connected key after setup." : "Team members will stop seeing AI-powered workspace actions.",
    });
  };

  return <div className="workspace-billing-panel">
    <section className="workspace-billing-welcome" aria-label="Current workspace plan">
      <div><span className="workspace-billing-eyebrow">Your workspace plan</span><div className="workspace-billing-plan-line"><strong>{plan}</strong><span className={`workspace-billing-status ${plan === "Workspace Pro" ? "is-pro" : ""}`}>Current plan</span></div><p>{plan === "Free" ? "You are on the free plan. Upgrade whenever your team needs more workspace capacity." : "You are on Workspace Pro, built for teams with growing operational needs."}</p></div>
      <dl className="workspace-billing-usage-list">
        <div><dt>People</dt><dd><strong>{usageLabel(seats)}</strong><span>in your workspace</span></dd></div>
        <div><dt>Projects</dt><dd><strong>{usageLabel(projects)}</strong><span>in your workspace</span></dd></div>
      </dl>
    </section>

    {canManageBilling ? <section className="panel workspace-billing-management-panel">
      <SectionHeader eyebrow="Billing controls" title="Turn on only what this workspace needs" text="Use the checkboxes to change capacity or AI access. Each change opens a confirmation with the exact impact before it is applied." />
      <div className="workspace-billing-management-options">
        <label className={`workspace-billing-addon-toggle workspace-billing-direct-toggle ${plan === "Workspace Pro" ? "is-active" : ""} ${freeDowngradeBlocked ? "is-locked" : ""}`.trim()}><input checked={plan === "Workspace Pro"} disabled={saving || freeDowngradeBlocked} onChange={(event) => openPlanConfirmation(event.target.checked)} type="checkbox" /><span><strong>Workspace Pro capacity</strong><small>{`₹${perEmployeeMonthlyAmount.toLocaleString("en-IN")} per additional active person above free quota`}</small><small>{freeDowngradeBlocked ? `Free unavailable at ${usageLabel(seats)} people and ${usageLabel(projects)} projects.` : plan === "Workspace Pro" ? "Uncheck to return to Free after usage is within free limits." : `Free includes ${seats.limit || 0} people and ${projects.limit || 0} project${Number(projects.limit || 0) === 1 ? "" : "s"}.`}</small></span></label>
        <label className={`workspace-billing-addon-toggle workspace-billing-direct-toggle ${byokEnabled ? "is-active" : ""}`.trim()}><input checked={byokEnabled} disabled={saving} onChange={(event) => openByokConfirmation(event.target.checked)} type="checkbox" /><span><strong>AI tools with your own API key</strong><small>{`Optional add-on · ₹${byokMonthlyAmount.toLocaleString("en-IN")}/month`}</small><small>Available with Free and Workspace Pro after confirmation.</small></span></label>
      </div>
      <div className="workspace-billing-management-footer"><p>{freeDowngradeBlocked ? "Downgrading to Free is hidden because current usage is above the free quota. Reduce usage first if you need to return to Free." : "Billing changes are applied only after you confirm the popup."}</p></div>
      {message ? <p className="project-settings-status" role="status">{message}</p> : null}
      {pendingBillingChange ? <div className="workspace-billing-confirmation" role="dialog" aria-modal="true" aria-labelledby="workspace-billing-confirmation-title">
        <div className="workspace-billing-confirmation-card">
          <span className="workspace-billing-eyebrow">Confirm billing change</span>
          <h3 id="workspace-billing-confirmation-title">{pendingBillingChange.title}</h3>
          <p>{pendingBillingChange.summary}</p>
          <div><strong>What changes</strong><span>{pendingBillingChange.impact}</span></div>
          <div className="workspace-dialog-actions"><button disabled={saving} onClick={() => setPendingBillingChange(null)} type="button">Cancel</button><button disabled={saving} onClick={applyBillingChange} type="button">{saving ? "Applying..." : pendingBillingChange.actionLabel}</button></div>
        </div>
      </div> : null}
    </section> : <section className="panel workspace-billing-readonly"><strong>Only a workspace owner or admin can change the plan or add AI tools.</strong><span>You can still see the current plan and usage above.</span></section>}
  </div>;
}

const accessBands = [
  {
    title: "Super admin",
    text: "Usually the first onboarded owner. Can assign or revoke all other admin access.",
  },
  {
    title: "HR admin",
    text: "Can manage employees, attendance, leaves, performance, documents, and HR workflows.",
  },
  {
    title: "Manager admin",
    text: "Can manage teams, project work, approvals, tickets, and operational reports for assigned areas.",
  },
  {
    title: "Finance admin",
    text: "Can manage invoices, payments, payouts, commissions, refunds, subscriptions, and finance reports.",
  },
];

const administrationTabPanels = {
  Overview: [
    ["Owner model", "First onboarded workspace owner controls super-admin access, billing, security, and role assignment."],
    ["Access reviews", "Review elevated users, disabled users, and stale permissions on a regular cadence."],
    ["Build status", "Track modules as planned, building, or live before exposing them broadly."],
  ],
  Workspace: [
    ["Workspace identity", "Name, branding, default modules, plan, and organization ownership."],
    ["Operating defaults", "Default statuses, record hygiene, project/client limits, and workspace-wide preferences."],
    ["Company structure", "Departments, teams, workspace clients, and project ownership rules."],
  ],
  Workflows: [
    ["Workflow ownership", "Only the workspace owner can publish ticket workflow changes."],
    ["Board categories", "Each status belongs to Backlog, To do, In progress, Review, or Done."],
    ["Safe changes", "Existing tickets retain their current status; review or migrate old statuses before removing them."],
  ],
  Users: [
    ["User lifecycle", "Invite, activate, disable, exit, and transfer access with permanent history."],
    ["Manager control", "Assign reporting managers, teams, and access scopes."],
    ["Session visibility", "Review active sessions and revoke access when needed."],
  ],
  Roles: [
    ["Role templates", "Super admin, HR admin, manager, finance admin, employee, and custom roles."],
    ["Custom roles", "Start from templates, then restrict by module, team, client, or action."],
    ["Role drift", "Spot users with mismatched role labels and permissions."],
  ],
  Permissions: [
    ["Action controls", "View, create, update, approve, delete, export, and admin permissions."],
    ["Scope controls", "Limit permissions by module, tab, team, project, client, or finance area."],
    ["Approval gates", "Require super-admin approval for sensitive access changes."],
  ],
  Integrations: [
    ["Calendar", "Google/Microsoft calendar sync, availability, rooms, and bookings."],
    ["Communication", "Email, chat, notifications, and meeting tools."],
    ["Storage and payment", "Files, payment providers, accounting, and client portal integrations."],
  ],
  Security: [
    ["Login policies", "SSO, MFA, allowed domains, device/session restrictions, and password policies."],
    ["Sensitive actions", "Extra confirmation for billing, security, role, and delete actions."],
    ["Data protection", "Export controls, audit retention, and private client data restrictions."],
  ],
  "Audit Logs": [
    ["Permanent trail", "Never delete admin, billing, permission, and security changes."],
    ["Actor context", "Store actor, IP, device, old value, new value, and timestamp."],
    ["Review workflow", "Filter logs by module, user, action, and time period."],
  ],
  Billing: [
    ["Plan control", "Plan, limits, renewal, invoices, and billing ownership."],
    ["Revenue links", "Workspace clients, projects, invoices, payments, and commissions."],
    ["Limits", "Free/paid plan limits, seats, projects, clients, and advanced modules."],
  ],
  Branding: [
    ["Company logo", "Logo shown in the sidebar, workspace shell, downloaded docs, and future client-facing reports."],
    ["Browser title icon", "Favicon or ICO file shown in the browser tab/title bar for this workspace client."],
    ["Fallback rule", "If no company asset is saved, Workspace automatically keeps the Lyriclious Workspace icon."],
  ],
  Automation: [
    ["Approval rules", "Role changes, payouts, leave, project approvals, and billing escalation."],
    ["Record automation", "Create tasks from won clients, overdue alerts, and status transitions."],
    ["Notifications", "Notify owners, managers, finance, clients, and admins at the right time."],
  ],
};

function TicketWorkflowForm({ workflows = [], canEdit = false, onUpdateAdministration }) {
  const [selectedType, setSelectedType] = useState("");
  const [workflowRows, setWorkflowRows] = useState(workflows);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [newType, setNewType] = useState("");

  useEffect(() => {
    setWorkflowRows(workflows);
    setSelectedType((current) => workflows.some((workflow) => workflow.workType === current) ? current : workflows[0]?.workType || "");
  }, [workflows]);
  const states = workflowRows.find((workflow) => workflow.workType === selectedType)?.states || [];
  const setStates = (updater) => setWorkflowRows((current) => current.map((workflow) => workflow.workType === selectedType ? { ...workflow, states: typeof updater === "function" ? updater(workflow.states || []) : updater } : workflow));

  const updateState = (index, patch) => setStates((current) => current.map((state, stateIndex) => stateIndex === index ? { ...state, ...patch } : state));
  const moveState = (index, direction) => setStates((current) => {
    const target = index + direction;
    if (target < 0 || target >= current.length) return current;
    const next = [...current]; [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const addType = () => {
    const workType = newType.trim().slice(0, 80);
    if (!workType) { setMessage("Enter a ticket type such as DEVOPS, CLOUD, OPS, or UX."); return; }
    if (workflowRows.some((workflow) => workflow.workType.toLowerCase() === workType.toLowerCase())) { setMessage("That ticket type already has a workflow."); return; }
    setWorkflowRows((current) => [...current, { workType, states: [{ id: "new", name: "New", category: "backlog", isInitial: true }] }]);
    setSelectedType(workType); setNewType(""); setMessage("");
  };
  const removeType = () => {
    if (workflowRows.length <= 1) return;
    setWorkflowRows((current) => current.filter((workflow) => workflow.workType !== selectedType));
    setSelectedType(workflowRows.find((workflow) => workflow.workType !== selectedType)?.workType || "");
  };
  const save = async (event) => {
    event.preventDefault();
    if (!canEdit) return;
    const invalidWorkflow = workflowRows.find((workflow) => !workflow.workType.trim() || !(workflow.states || []).length || !(workflow.states || []).some((state) => state.isInitial) || (workflow.states || []).some((state) => !state.name.trim()));
    if (invalidWorkflow) { setMessage("Every ticket type needs at least one named status and exactly one first status."); return; }
    setSaving(true);
    setMessage("");
    try {
      await onUpdateAdministration?.({ ticketWorkflows: workflowRows });
      setMessage("Ticket workflow published.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not save the ticket workflow.");
    } finally { setSaving(false); }
  };

  return <section className="panel ticket-workflow-admin-panel"><SectionHeader eyebrow="Workflows" title="Ticket lifecycle by type" text="Create client-specific types such as DEVOPS or CLOUD, then define their ordered statuses and first state." /><form onSubmit={save}><div className="ticket-workflow-topbar"><label className="ticket-workflow-type-picker">Ticket type<select disabled={!canEdit || saving} onChange={(event) => setSelectedType(event.target.value)} value={selectedType}>{workflowRows.map((workflow) => <option key={workflow.workType}>{workflow.workType}</option>)}</select></label>{canEdit ? <div className="ticket-workflow-new-type"><input aria-label="New ticket type" disabled={saving} onChange={(event) => setNewType(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addType(); } }} placeholder="Add type, e.g. DEVOPS" value={newType} /><button disabled={saving || !newType.trim()} onClick={addType} type="button">Add type</button><button className="workflow-remove-type" disabled={saving || workflowRows.length <= 1} onClick={removeType} type="button">Remove type</button></div> : null}</div><div className="ticket-workflow-admin-list">{states.map((state, index) => <article key={`${state.id}-${index}`}><div className="ticket-workflow-order"><span>{index + 1}</span><button aria-label={`Move ${state.name || "status"} up`} disabled={!canEdit || saving || index === 0} onClick={() => moveState(index, -1)} type="button">↑</button><button aria-label={`Move ${state.name || "status"} down`} disabled={!canEdit || saving || index === states.length - 1} onClick={() => moveState(index, 1)} type="button">↓</button></div><input aria-label="Status name" disabled={!canEdit || saving} onChange={(event) => updateState(index, { name: event.target.value })} value={state.name} /><select aria-label="Board category" disabled={!canEdit || saving} onChange={(event) => updateState(index, { category: event.target.value })} value={state.category}><option value="backlog">Backlog</option><option value="todo">To do</option><option value="in_progress">In progress</option><option value="review">Review</option><option value="done">Done</option></select><label><input checked={state.isInitial === true} disabled={!canEdit || saving} onChange={() => setStates((current) => current.map((item, itemIndex) => ({ ...item, isInitial: itemIndex === index })))} type="radio" name={`initial-ticket-status-${selectedType}`} /> First status</label><button aria-label={`Remove ${state.name}`} disabled={!canEdit || saving || states.length === 1} onClick={() => setStates((current) => current.filter((_, itemIndex) => itemIndex !== index))} type="button">Remove</button></article>)}</div>{canEdit ? <div className="workspace-dialog-actions"><button onClick={() => setStates((current) => [...current, { id: `status-${Date.now()}`, name: "New status", category: "todo", isInitial: false }])} type="button">Add status</button><button disabled={saving || !onUpdateAdministration} type="submit">{saving ? "Publishing…" : "Publish workflows"}</button></div> : null}{message ? <p className="branding-admin-status" role="status">{message}</p> : null}{canEdit ? <p className="ticket-workflow-safety-note">Existing tickets must be moved before you remove their type or current status. This protects active work from losing its workflow.</p> : null}</form></section>;
}

export function BrandingForm({ branding = null, onUpdateBranding, saveStatus = "" }) {
  const [draft, setDraft] = useState({
    logoUrl: "",
    logoLongUrl: "",
    logoSquareUrl: "",
    faviconUrl: "",
    faviconIcoUrl: "",
    themeMode: "default",
    colorThemeMode: "default",
    primaryColor: "#7c3aed",
  });
  const [message, setMessage] = useState("");
  const [uploadingField, setUploadingField] = useState("");

  useEffect(() => {
    setDraft({
      logoUrl: branding?.logo_url || "",
      logoLongUrl: branding?.logo_long_url || "",
      logoSquareUrl: branding?.logo_square_url || "",
      faviconUrl: branding?.favicon_url || "",
      faviconIcoUrl: branding?.favicon_ico_url || "",
      themeMode: branding?.color_theme_mode || branding?.theme_mode || "default",
      colorThemeMode: branding?.color_theme_mode || branding?.theme_mode || "default",
      primaryColor: branding?.primary_color || "#7c3aed",
    });
  }, [branding?.color_theme_mode, branding?.favicon_ico_url, branding?.favicon_url, branding?.logo_long_url, branding?.logo_square_url, branding?.logo_url, branding?.primary_color, branding?.theme_mode]);

  const previewSquareLogo = draft.logoSquareUrl || draft.logoUrl || DEFAULT_WORKSPACE_ICON;
  const previewLongLogo = draft.logoLongUrl || DEFAULT_WORKSPACE_ICON;
  const previewFavicon = draft.faviconIcoUrl || draft.faviconUrl || DEFAULT_WORKSPACE_ICON;

  const handleAssetUpload = async (field, file) => {
    if (!file) return;
    setMessage("");
    setUploadingField(field);
    try {
      const asset = await readFileAsDataUrl(file);
      setDraft((current) => ({ ...current, [field]: asset }));
    } catch (error) {
      setMessage(error?.message || "Could not read selected file.");
    } finally {
      setUploadingField("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");
    if (!onUpdateBranding) {
      setMessage("Branding save is not available for this account.");
      return;
    }
    try {
      await onUpdateBranding(draft);
      setMessage("Company branding saved.");
    } catch (error) {
      setMessage(error?.response?.data?.message || "Could not save branding.");
    }
  };

  return (
    <section className="panel branding-admin-panel">
      <SectionHeader
        eyebrow="Workspace Branding"
        title="Logo and browser title icon"
        text="Ask each workspace client for their company logo and favicon/ICO asset. If they do not provide one, the Lyriclious Workspace icon stays as the fallback."
      />
      <form className="branding-admin-form" onSubmit={handleSubmit}>
        <div className="branding-preview-grid">
          <article className="branding-preview-card">
            <span>Square logo preview</span>
            <div className="branding-sidebar-preview">
              <img alt="" src={previewSquareLogo} />
              <div>
                <strong>Client Workspace</strong>
                <small>admin@company.com</small>
              </div>
            </div>
          </article>
          <article className="branding-preview-card">
            <span>Long logo preview</span>
            <div className="branding-title-preview">
              <img alt="" className="branding-long-logo-preview" src={previewLongLogo} />
              <strong>Lyriclious Workspace</strong>
            </div>
          </article>
          <article className="branding-preview-card">
            <span>Browser icon preview</span>
            <div className="branding-title-preview">
              <img alt="" src={previewFavicon} />
              <strong>Client Workspace | Lyriclious Workspace</strong>
            </div>
          </article>
        </div>

        <div className="branding-asset-grid">
          <label className="branding-asset-field">
            <span>Long logo</span>
            <input
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(event) => handleAssetUpload("logoLongUrl", event.target.files?.[0])}
              type="file"
            />
            <small>Used for wide brand placements across the workspace.</small>
          </label>
          <label className="branding-asset-field">
            <span>Square logo</span>
            <input
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(event) => handleAssetUpload("logoSquareUrl", event.target.files?.[0])}
              type="file"
            />
            <small>Used in sidebar, header, and compact workspace chrome.</small>
          </label>
          <label className="branding-asset-field">
            <span>Browser icon</span>
            <input
              accept=".ico,image/x-icon,image/vnd.microsoft.icon,image/png"
              onChange={(event) => handleAssetUpload("faviconIcoUrl", event.target.files?.[0])}
              type="file"
            />
            <small>Used in the browser tab title and other favicon surfaces.</small>
          </label>
        </div>

        <label>
          Brand color source
          <select
            onChange={(event) => setDraft((current) => ({ ...current, themeMode: event.target.value, colorThemeMode: event.target.value }))}
            value={draft.colorThemeMode || draft.themeMode}
          >
            <option value="default">Lyriclious default</option>
            <option value="organization">Organization color</option>
          </select>
        </label>
        <label>
          Organization color
          <input
            onChange={(event) => setDraft((current) => ({ ...current, primaryColor: event.target.value }))}
            type="color"
            value={draft.primaryColor}
          />
        </label>

        <p className="branding-admin-note">
          Upload files directly here. Leave the branding assets blank to keep the Lyriclious icon and default branding fallback.
        </p>
        {uploadingField ? <p className="branding-admin-status">Preparing {uploadingField.replace(/Url$/, "").replace(/[A-Z]/g, (match) => ` ${match.toLowerCase()}`).trim()}…</p> : null}
        {message || saveStatus ? <p className="branding-admin-status">{message || saveStatus}</p> : null}
        <div className="workspace-dialog-actions">
          <button
            onClick={() => setDraft((current) => ({
              ...current,
              logoUrl: "",
              logoLongUrl: "",
              logoSquareUrl: "",
              faviconUrl: "",
              faviconIcoUrl: "",
            }))}
            type="button"
          >
            Use Lyriclious fallback
          </button>
          <button disabled={!onUpdateBranding} type="submit">Save branding</button>
        </div>
      </form>
    </section>
  );
}

function AdministrationOverview({ administration, currentUser, onNavigateModule, onRefresh, workspaceInfo }) {
  const overview = administration?.overview || {};
  const modules = administration?.modules || [];
  const workflows = administration?.ticketWorkflows || [];
  const isOwner = Boolean(overview.owner?.id && `${overview.owner.id}` === `${currentUser?.id || currentUser?._id || ""}`);
  const invalidWorkflows = workflows.filter((workflow) => !(workflow.states || []).some((state) => state.isInitial));
  const activePeople = overview.people?.active;
  const availableModules = modules.filter((module) => module.enabled !== false).length;
  const liveModules = modules.filter((module) => module.enabled !== false && module.status === "live").length;
  const attentionModules = modules.filter((module) => module.enabled === false || module.status !== "live" || module.premium === true).slice(0, 6);
  const seatUsage = overview.usage?.seats || {};
  const projectUsage = overview.usage?.projects || {};
  const displayedPlan = getDisplayedWorkspacePlan(workspaceInfo?.plan);
  const quickActions = [
    ["Manage workspace", "Administration", "Workspace"],
    ["Configure workflows", "Administration", "Workflows"],
    ["Manage people", "People", "Directory"],
    ["Manage roles", "Administration", "Roles"],
    ["Manage permissions", "Administration", "Permissions"],
    ["Update branding", "Administration", "Branding"],
    ["Review billing", "Administration", "Billing"],
    ["Review security", "Administration", "Security"],
  ];

  return <div className="administration-overview">
    <div className="administration-overview-toolbar"><span>Workspace administration</span><button aria-label="Refresh administration overview" className="administration-refresh-button" disabled={!onRefresh} onClick={onRefresh} title="Refresh overview" type="button">↻</button></div>

    <section className="administration-overview-metrics" aria-label="Workspace administration status">
      <article><span>Seat usage</span><strong>{seatUsage.used ?? activePeople ?? "—"}{seatUsage.limit ? ` / ${seatUsage.limit}` : ""}</strong><small>{overview.people?.elevated ?? "—"} active elevated people</small></article>
      <article><span>Project usage</span><strong>{projectUsage.used ?? "—"}{projectUsage.limit ? ` / ${projectUsage.limit}` : ""}</strong><small>{displayedPlan} workspace plan</small></article>
      <article><span>Module health</span><strong>{liveModules} / {availableModules}</strong><small>{attentionModules.length ? `${attentionModules.length} need review` : "All enabled modules live"}</small></article>
      <article><span>Workflow health</span><strong>{workflows.length}</strong><small>{invalidWorkflows.length ? `${invalidWorkflows.length} missing an initial status` : "All work types ready"}</small></article>
    </section>

    <section className="administration-overview-grid">
      <article className="panel administration-overview-panel">
        <SectionHeader eyebrow="Access health" title="Workspace control" text="Current owner and access configuration." />
        <dl className="administration-data-list">
          <div><dt>Workspace owner</dt><dd>{overview.owner?.name || "Not available"}{overview.owner?.email ? <small>{overview.owner.email}</small> : null}</dd></div>
          <div><dt>Active people</dt><dd>{activePeople ?? "—"}<small>{overview.people?.total ?? "—"} total profiles</small></dd></div>
          <div><dt>Elevated people</dt><dd>{overview.people?.elevated ?? "—"}<small>Owner, admin, or manager</small></dd></div>
          <div><dt>Role templates</dt><dd>{administration?.roleTemplates?.length || 0}<small>Saved workspace roles</small></dd></div>
          <div><dt>Access policies</dt><dd>{administration?.accessPolicies?.length || 0}<small>Module and role policies</small></dd></div>
        </dl>
      </article>
      <article className="panel administration-overview-panel">
        <SectionHeader eyebrow="Readiness" title="Security and audit" text="Only implemented controls are reported here." />
        <dl className="administration-data-list">
          <div><dt>Security policies</dt><dd>{overview.readiness?.securityPolicies ? "Configured" : "Not configured yet"}<small>Open Security to review available controls</small></dd></div>
          <div><dt>Audit logs</dt><dd>{overview.readiness?.auditLogs ? "Available" : "Not configured yet"}<small>Open Audit Logs when retention is enabled</small></dd></div>
          <div><dt>Branding</dt><dd>{workspaceInfo?.branding?.logo_square_url || workspaceInfo?.branding?.logo_url ? "Custom" : "Default"}<small>{workspaceInfo?.branding?.favicon_ico_url || workspaceInfo?.branding?.favicon_url ? "Browser icon set" : "Lyriclious fallback"}</small></dd></div>
          <div><dt>Plan</dt><dd>{displayedPlan}<small>Billing is managed by Lyriclious</small></dd></div>
        </dl>
      </article>
    </section>

    <section className="administration-overview-grid">
      <article className="panel administration-overview-panel">
        <SectionHeader eyebrow="Module health" title="Modules needing review" text="Enabled, planned, building, and premium access from this workspace configuration." />
        <div className="administration-status-list">{attentionModules.map((module) => <div key={module.key}><span>{module.label || module.key}</span><small>{module.enabled === false ? "Disabled" : module.premium ? "Premium access" : module.status || "Needs review"}</small></div>)}{!attentionModules.length ? <p className="empty-state">No modules need attention.</p> : null}</div>
      </article>
      <article className="panel administration-overview-panel">
        <SectionHeader eyebrow="Workflow health" title="Ticket work types" text="Each work type needs one initial status before new tickets can start reliably." />
        <div className="administration-status-list">{workflows.map((workflow) => <div key={workflow.workType}><span>{workflow.workType}</span><small className={(workflow.states || []).some((state) => state.isInitial) ? "is-ready" : "is-attention"}>{(workflow.states || []).some((state) => state.isInitial) ? "Ready" : "Initial status missing"}</small></div>)}{!workflows.length ? <p className="empty-state">No ticket workflows configured.</p> : null}</div>
      </article>
    </section>

    {isOwner ? <section className="panel administration-overview-panel">
      <SectionHeader eyebrow="Workspace actions" title="Manage this workspace" text="Open the focused setting without leaving the workspace." />
      <div className="administration-quick-actions">{quickActions.map(([label, moduleName, tabName]) => <button key={label} onClick={() => onNavigateModule(moduleName, tabName)} type="button">{label}</button>)}</div>
    </section> : null}
  </div>;
}

export function AdministrationView({ activeTab = "Overview", administration = null, workspaceInfo = null, branding = null, brandingSaveStatus = "", currentUser = null, error = "", isLoading = false, onNavigateModule = () => {}, onRefresh, onUpdateBranding, onUpdateAdministration }) {
  const administrationModules = administration?.modules;
  const modules = useMemo(() => administrationModules || [], [administrationModules]);
  const roleTemplateRows = administration?.roleTemplates || [];
  const accessPolicies = administration?.accessPolicies || [];
  const liveModules = modules.filter((module) => module.status === "live").length;
  const buildingModules = modules.filter((module) => module.status === "building").length;
  const plannedModules = modules.filter((module) => module.status === "planned").length;

  if (activeTab === "Overview") {
    if (isLoading) return <section aria-busy="true" className="panel"><p className="empty-state">Loading workspace administration…</p></section>;
    if (error) return <section className="panel" role="alert"><p className="empty-state">{error}</p></section>;
    return <AdministrationOverview administration={administration} currentUser={currentUser} onNavigateModule={onNavigateModule} onRefresh={onRefresh} workspaceInfo={workspaceInfo} />;
  }

  if (activeTab === "Workspace") {
    if (isLoading) return <section aria-busy="true" className="panel"><p className="empty-state">Loading workspace configuration…</p></section>;
    if (error) return <section className="panel" role="alert"><p className="empty-state">{error}</p></section>;
    return <WorkspaceAdministrationPanel administration={administration} currentUser={currentUser} onNavigateModule={onNavigateModule} onRefresh={onRefresh} onSave={onUpdateAdministration} workspaceInfo={workspaceInfo} />;
  }

  if (activeTab === "Workflows") {
    if (isLoading) return <section aria-busy="true" className="panel"><p className="empty-state">Loading ticket workflows…</p></section>;
    if (error) return <section className="panel" role="alert"><p className="empty-state">{error}</p></section>;
    const isOwner = currentUser?.accessLevel === "Owner";
    return <div className="workspace-administration-panel"><TicketWorkflowForm canEdit={isOwner} workflows={administration?.ticketWorkflows || []} onUpdateAdministration={onUpdateAdministration} /></div>;
  }

  if (activeTab === "Billing") {
    if (isLoading) return <section aria-busy="true" className="panel"><p className="empty-state">Loading workspace billing…</p></section>;
    if (error) return <section className="panel" role="alert"><p className="empty-state">{error}</p></section>;
    return <WorkspaceBillingPanel administration={administration} currentUser={currentUser} onSave={onUpdateAdministration} workspaceInfo={workspaceInfo} />;
  }

  return (
    <>
      <section className="dashboard-action-grid">
        {accessBands.map((band, index) => {
          const Icon = index === 0 ? AdminPanelSettings : index === 1 ? LockPerson : index === 2 ? Groups : ManageAccounts;
          return (
            <article key={band.title}>
              <Icon fontSize="small" />
              <strong>{band.title}</strong>
              <span>{band.text}</span>
            </article>
          );
        })}
      </section>

      <section className="panel">
        <SectionHeader
          eyebrow={`${activeTab} Plan`}
          title={`${activeTab} required controls`}
          text="This section shows what needs to be implemented or monitored for this administration area."
        />
        <div className="settings-grid">
          {(administrationTabPanels[activeTab] || administrationTabPanels.Overview).map(([title, text]) => (
            <article key={title}>
              <strong>{title}</strong>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>

      {activeTab === "Branding" ? (
        <BrandingForm
          branding={branding}
          onUpdateBranding={onUpdateBranding}
          saveStatus={brandingSaveStatus}
        />
      ) : null}

      <section className="functional-metrics general-metrics" aria-label="Administration backend summary">
        <article>
          <span>Modules</span>
          <strong>{modules.length || 12}</strong>
          <small>{liveModules} live · {buildingModules} building · {plannedModules} planned</small>
        </article>
        <article>
          <span>Role templates</span>
          <strong>{roleTemplateRows.length || roleTemplates.length}</strong>
          <small>Stored on workspace DB</small>
        </article>
        <article>
          <span>Access policies</span>
          <strong>{accessPolicies.length}</strong>
          <small>Module + role actions</small>
        </article>
        <article>
          <span>Owner control</span>
          <strong>On</strong>
          <small>Updates restricted to owner</small>
        </article>
      </section>

      <section className="split-layout">
        <div className="panel">
          <SectionHeader
            eyebrow="Scoped Access"
            title="Different admins can see different controls"
            text="A super admin can give HR only HR tabs, managers only project/team controls, finance only payment controls, and CRM admins only client-facing controls."
          />
          <div className="settings-grid single-column">
            {settingsGroups.map((group) => (
              <article key={group.title}>
                <strong>{group.title}</strong>
                <div className="chip-row">
                  {group.items.map((item) => (
                    <span key={item}>{item}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="panel">
          <SectionHeader
            eyebrow="Role Templates"
            title="Start with roles, then customize"
            text="Role templates are only a starting point. Final access should be editable per person and per workspace area."
          />
          <div className="role-grid">
            {(roleTemplateRows.length ? roleTemplateRows : roleTemplates.map((role) => ({ name: role }))).map((role) => (
              <span key={role.name}>{role.name}</span>
            ))}
          </div>
        </div>
      </section>

      {roleTemplateRows.length ? (
        <section className="panel">
          <SectionHeader
            eyebrow="Database Roles"
            title="Role templates saved for this workspace"
            text="These templates will drive the next pass: invite users, assign roles, and restrict each section by action-level access."
          />
          <div className="permission-list settings-permissions">
            {roleTemplateRows.map((role) => (
              <article key={role.name}>
                <strong>{role.name}</strong>
                <p>{role.description}</p>
                <div className="chip-row">
                  {role.permissions?.map((permission) => (
                    <span key={permission}>{permission}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="panel">
        <SectionHeader
          eyebrow="Permissions"
          title="Action-level permission map"
          text="Permissions should decide who can view, create, update, approve, delete, export, invite, bill, automate, or administrate each part of the workspace."
        />
        <div className="permission-list settings-permissions">
          {permissionGroups.map((group) => (
            <article key={group.title}>
              <strong>{group.title}</strong>
              <p>{group.permissions.join(" · ")}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}
